@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

@tailwind base;
@tailwind components;
@tailwind utilities;

@layer base {
  html {
    scroll-behavior: smooth;
  }

  /* Noesis Frameworks: Archival Paper (#F4F4F0) Default Bright Mode */
  body {
    background-color: #F4F4F0;
    background-image: 
      radial-gradient(rgba(0, 0, 0, 0.04) 1px, transparent 1px);
    background-size: 24px 24px;
    color: #0B0C0E;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    letter-spacing: -0.012em;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    min-height: 100vh;
  }

  /* Noesis Frameworks: Obsidian (#0B0C0E) Dark Mode */
  html.dark body {
    background-color: #0B0C0E;
    background-image: 
      radial-gradient(rgba(255, 85, 0, 0.08) 1px, transparent 1px),
      radial-gradient(ellipse 80% 40% at 50% -5%, rgba(255, 85, 0, 0.08), transparent 70%);
    background-size: 24px 24px, 100% 100%;
    color: #F4F4F0;
  }

  /* Scrollbar */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: rgba(255, 85, 0, 0.25);
    border-radius: 9999px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 85, 0, 0.5);
  }
}

@layer components {
  /* Cards: Pure white with warm stone border in Archival, #131519 with subtle border in Obsidian */
  .card {
    background-color: #FFFFFF;
    border: 1px solid #DCDCD4;
    border-radius: 16px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.03), 0 1px 2px -1px rgba(0, 0, 0, 0.03);
  }
  html.dark .card {
    background-color: #131519;
    border: 1px solid #262930;
    box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.5), inset 0 1px 0 0 rgba(255, 255, 255, 0.04);
  }

  .card-subtle {
    background-color: #FAF9F5;
    border: 1px solid #DCDCD4;
    border-radius: 12px;
  }
  html.dark .card-subtle {
    background-color: #181B21;
    border: 1px solid #262930;
  }

  /* Noesis Electric Orange Primary Button */
  .btn-primary {
    @apply inline-flex items-center justify-center gap-2 rounded-lg bg-[#FF5500] px-4 py-2 text-xs font-semibold text-white
           shadow-sm shadow-orange-500/20 transition-all duration-150 hover:bg-[#EA4B00] active:scale-[0.98]
           disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100
           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2;
  }

  .btn-secondary {
    @apply inline-flex items-center justify-center gap-2 rounded-lg border border-[#DCDCD4] bg-white px-4 py-2
           text-xs font-medium text-stone-700 transition-all duration-150
           hover:bg-stone-50 hover:text-stone-900 active:scale-[0.98]
           disabled:cursor-not-allowed disabled:opacity-40
           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400;
  }
  html.dark .btn-secondary {
    @apply border-[#262930] bg-[#181B21] text-slate-200 hover:bg-white/[0.08] hover:text-white;
  }

  .btn-ghost {
    @apply inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium
           text-stone-600 transition-colors duration-150 hover:text-orange-600 hover:bg-orange-50
           disabled:cursor-not-allowed disabled:opacity-40;
  }
  html.dark .btn-ghost {
    @apply text-slate-400 hover:text-orange-400 hover:bg-orange-500/10;
  }

  .btn-danger {
    @apply inline-flex items-center justify-center gap-1.5 rounded-lg border border-rose-300 bg-rose-50 px-3 py-1.5
           text-xs font-medium text-rose-700 transition-colors duration-150 hover:bg-rose-100;
  }
  html.dark .btn-danger {
    @apply border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20;
  }

  /* Form Inputs */
  .input {
    @apply w-full rounded-lg border border-[#DCDCD4] bg-white px-3.5 py-2 text-xs text-stone-900
           placeholder:text-stone-400 outline-none transition-all duration-150
           focus:border-[#FF5500] focus:ring-1 focus:ring-[#FF5500]
           disabled:cursor-not-allowed disabled:opacity-40;
  }
  html.dark .input {
    @apply border-[#262930] bg-[#0B0C0E] text-slate-100 placeholder:text-slate-500;
  }

  .label {
    @apply mb-1.5 flex items-center justify-between text-xs font-medium text-stone-700;
  }
  html.dark .label {
    @apply text-slate-300;
  }

  /* Status Badges */
  .badge-approved {
    @apply inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-800;
  }
  html.dark .badge-approved {
    @apply border-emerald-500/30 bg-emerald-500/15 text-emerald-300;
  }

  .badge-denied {
    @apply inline-flex items-center gap-1.5 rounded-full border border-rose-300 bg-rose-50 px-2.5 py-0.5 text-xs font-medium text-rose-800;
  }
  html.dark .badge-denied {
    @apply border-rose-500/30 bg-rose-500/15 text-rose-300;
  }

  .badge-escalated {
    @apply inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800;
  }
  html.dark .badge-escalated {
    @apply border-amber-500/30 bg-amber-500/15 text-amber-300;
  }

  .badge-info {
    @apply inline-flex items-center gap-1.5 rounded-full border border-orange-300 bg-orange-50 px-2.5 py-0.5 text-xs font-medium text-orange-800;
  }
  html.dark .badge-info {
    @apply border-orange-500/30 bg-orange-500/15 text-orange-300;
  }

  .badge-neutral {
    @apply inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-stone-100 px-2.5 py-0.5 text-xs font-medium text-stone-700;
  }
  html.dark .badge-neutral {
    @apply border-white/[0.1] bg-white/[0.04] text-slate-300;
  }
}

/* Ambient dot grid for interactive canvases */
.bg-dot-grid {
  background-image: radial-gradient(rgba(255, 85, 0, 0.15) 1px, transparent 1px);
  background-size: 20px 20px;
}

/* Glowing pulse along pipeline SVG lines */
@keyframes flowDash {
  to {
    stroke-dashoffset: -32;
  }
}

.animate-flow-dash {
  stroke-dasharray: 8 8;
  animation: flowDash 1.4s linear infinite;
}
